// Point-forecast read (contextual-overview design D7): the server-cached
// Open-Meteo numbers (api_contracts §5.6). Stale-while-revalidate on the
// server; the client polls gently and renders `fetchedAt` age honestly.
// The on-map weather PICTURE is MapTiler's (components/map) — these are
// the numbers at the boat's position.

import { useEffect, useState } from 'react';
import { neoPageHourlyMax, neoPageHourlySeries } from '@neomaris/protocol';

import { api } from '../api';

export interface ForecastHourlyBlock {
  hourly?: Record<string, unknown>;
  hourly_units?: Record<string, string>;
  /** Present on the forecast half only, and only on rows cached since ADR 0200. */
  daily?: Record<string, unknown>;
  daily_units?: Record<string, string>;
}

export interface ForecastResponse {
  status: 'ready' | 'pending' | 'no_position' | 'stale_position';
  positionAt?: string;
  fetchedAt?: string;
  latRounded?: number;
  lonRounded?: number;
  source?: string;
  forecast?: ForecastHourlyBlock | null;
  marine?: ForecastHourlyBlock | null;
}

export interface ForecastState {
  kind: 'loading' | 'ready' | 'unavailable';
  data: ForecastResponse | null;
}

const READY_POLL_MS = 5 * 60_000;
const PENDING_POLL_MS = 20_000;

export function useForecast(boatId: string, enabled: boolean): ForecastState {
  const [state, setState] = useState<ForecastState>({ kind: 'loading', data: null });

  useEffect(() => {
    if (!enabled) {
      setState({ kind: 'unavailable', data: null });
      return;
    }
    let alive = true;
    let timer: number | null = null;
    async function load() {
      try {
        const res = await api<ForecastResponse>(`/v1/boats/${boatId}/forecast`);
        if (!alive) return;
        if (res.status === 'ready') {
          setState({ kind: 'ready', data: res });
          timer = window.setTimeout(() => void load(), READY_POLL_MS);
        } else if (res.status === 'pending') {
          setState({ kind: 'loading', data: res });
          timer = window.setTimeout(() => void load(), PENDING_POLL_MS);
        } else {
          setState({ kind: 'unavailable', data: res });
          timer = window.setTimeout(() => void load(), PENDING_POLL_MS);
        }
      } catch {
        if (!alive) return;
        setState({ kind: 'unavailable', data: null });
        timer = window.setTimeout(() => void load(), READY_POLL_MS);
      }
    }
    void load();
    return () => {
      alive = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [boatId, enabled]);

  return state;
}

/** Hourly arrays → chart points over [fromMs, toMs]; null-safe on gaps. */
export function hourlySeries(
  block: ForecastHourlyBlock | null | undefined,
  key: string,
  fromMs: number,
  toMs: number
): Array<{ t: number; v: number | null }> {
  // The forecast module page's figures read the same points (TOM-665).
  return neoPageHourlySeries(block, key, fromMs, toMs);
}

/** Largest value of a key inside a window — the squall-risk (CAPE) probe. */
export function hourlyMax(
  block: ForecastHourlyBlock | null | undefined,
  key: string,
  fromMs: number,
  toMs: number
): number | null {
  return neoPageHourlyMax(block, key, fromMs, toMs);
}

/** The value at (or nearest after) `atMs` — "sea state now". */
export function hourlyAt(
  block: ForecastHourlyBlock | null | undefined,
  key: string,
  atMs: number
): number | null {
  const pts = hourlySeries(block, key, atMs - 3_600_000, atMs + 2 * 3_600_000);
  for (const p of pts) {
    if (p.t >= atMs - 1_800_000 && p.v !== null) return p.v;
  }
  return pts.find((p) => p.v !== null)?.v ?? null;
}
